import {
  ChangeDetectorRef,
  Component,
  OnChanges,
  SimpleChanges,
  ViewEncapsulation,
  ChangeDetectionStrategy,
  inject,
  output,
  input
} from '@angular/core';
import { SbSelectItem } from '../sb-select/sb-select-base.component';
import { SbComplexColumn, SbComplexMultipleComponent } from '../sb-complex-multiple/sb-complex-multiple.component';
import {
  SbSchemaField,
  SbSchemaFieldMappingGroup,
  SbSchemaFieldMappingRow,
  SbSchemaForm,
  SbSchemaFormOptions,
  SbSchemaSegment,
  SbSchemaServerErrors,
} from './sb-schema-form.types';
import {
  conditionNeedsValue,
  conditionOperators,
  isBlank,
  isVisible,
  keptRowIndexes,
  keptRuleIndexes,
  prepareValues,
  SB_SCHEMA_CONDITION_MAX_RULES,
  validateValues,
} from './sb-schema-form.validation';
import { NgTemplateOutlet } from '@angular/common';
import { SbInputFormSectionComponent } from '../sb-form/sb-form.component';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { SbInputComplexRowDirective } from '../sb-complex-multiple/sb-complex-row.directive';
import { SbCopyFieldComponent } from '../sb-copy-field/sb-copy-field.component';
import { SbInputSelectComponent } from '../sb-select/sb-select.component';
import { SbInputTextComponent } from '../sb-input/sb-input-text.component';
import { SbInputPasswordComponent } from '../sb-input/sb-input-password.component';
import { SbInputNumberComponent } from '../sb-input/sb-input-number.component';
import { SbCheckboxComponent } from '../sb-checkbox/sb-checkbox.component';
import { SbRadioComponent } from '../sb-radio/sb-radio.component';
import { SbTextareaComponent } from '../sb-textarea/sb-textarea.component';

export const SB_SCHEMA_CONFIGURED_SECRET = 'Configured — leave blank to keep the current value';

/** The section the read-only field mapping closes; it is added after `segments` when the schema has none. */
export const SB_SCHEMA_FIELD_MAPPING_SECTION = 'mapping';

/** Contact type names by influencer type slug. */
const CONTACT_TYPES: Record<string, string> = { attendee: 'Attendee', speaker: 'Speaker', partner: 'Partner' };
const CONDITION_SUB_KEYS = ['field', 'operator', 'value'];

const EMPTY_FORM: SbSchemaForm = { sections: [], fields: [], rules: [] };
/** A segment select inside the segment mapping table: its row already names the group. */
export const SB_SCHEMA_SEGMENT_PLACEHOLDER = 'Select segment';
const NO_ITEMS: SbSelectItem[] = [];
const ROW_PATH = /^([A-Za-z][A-Za-z0-9_]*)\.(\d+)\.([A-Za-z][A-Za-z0-9_]*)$/;

type Values = Record<string, unknown>;

let nextMappedId = 0;
type RowPatch = (partial: Values) => void;

/**
 * The control a field type renders with. A url renders as text: the schema's own check decides,
 * not `app-sb-input-url`'s stricter one, whose message repeats what was typed.
 */
export type SbSchemaControl = 'text' | 'secret' | 'number' | 'boolean' | 'radio' | 'select' | 'textarea';

/**
 * One entry of a section: a field, or a run of consecutive segment fields (`segments`) or field-name
 * settings (`mapsTo`) that render together as one fixed-row table.
 */
export interface SbSchemaItem {
  key: string;
  kind: 'field' | 'segments' | 'mapsTo';
  field: SbSchemaField | null;
  fields: SbSchemaField[];
}

interface SbSchemaSectionView {
  key: string;
  title: string;
  description: string;
  fields: SbSchemaField[];
  items: SbSchemaItem[];
}

/** The read-only field mapping of one contact type. */
export interface SbSchemaMappedGroup {
  influencerType: string;
  title: string;
  rows: SbSchemaFieldMappingRow[];
}

/**
 * Renders a server-described settings form (`SbSchemaForm`) with the unified controls and
 * validates it with the server's own rules and messages, so the panel refuses what a save would.
 *
 * The kit's controls do not read their parent control's errors, so every message is computed here
 * and bound to the control's `error` once the field was left or `validate()` ran. A refused save's
 * field messages (`setServerErrors`) stay on their fields until that field changes; form-wide
 * messages are exposed through `formErrors` / `formErrorsChange` for the host's banner.
 */
@Component({
  selector: 'app-sb-schema-form',
  templateUrl: './sb-schema-form.component.html',
  styleUrls: ['./sb-schema-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgTemplateOutlet, SbInputFormSectionComponent, SbComplexMultipleComponent, ReactiveFormsModule, FormsModule, SbInputComplexRowDirective, SbCopyFieldComponent, SbInputSelectComponent, SbInputTextComponent, SbInputPasswordComponent, SbInputNumberComponent, SbCheckboxComponent, SbRadioComponent, SbTextareaComponent]
})
export class SbSchemaFormComponent implements OnChanges {
  private readonly cdr = inject(ChangeDetectorRef);

  readonly schema = input<SbSchemaForm | null>(null);
  readonly values = input<Values | null>(null);
  readonly options = input<SbSchemaFormOptions | null>(null);
  readonly serverErrors = input<SbSchemaServerErrors | null>(null);
  /** The plugin's fixed field mapping (the settings payload's `field_mapping`), shown read-only. */
  readonly fieldMapping = input<SbSchemaFieldMappingGroup[] | null>(null);

  /** The typed value (see `value`) after every edit. */
  readonly valueChange = output<Values>();
  /** `formErrors` whenever it changes; a change made by an input arrives a tick later (see `formErrors`). */
  readonly formErrorsChange = output<string[]>();

  /** What the controls show and edit, keyed by field. `value` turns it into what a save sends. */
  model: Values = {};
  sectionViews: SbSchemaSectionView[] = [];
  /** Fields with no section, or one the schema does not declare. */
  looseFields: SbSchemaField[] = [];
  looseItems: SbSchemaItem[] = [];
  /** `fieldMapping` by contact type, without the rows a rendered setting edits. */
  mappedGroups: SbSchemaMappedGroup[] = [];
  /** The read-only mapping starts collapsed behind its toggle. */
  mappedExpanded = false;
  readonly mappedId = `sb-schema-form-mapped-${++nextMappedId}`;
  readonly configuredSecretHint = SB_SCHEMA_CONFIGURED_SECRET;
  submitted = false;

  readonly fieldMappingSection = SB_SCHEMA_FIELD_MAPPING_SECTION;
  readonly segmentColumns: SbComplexColumn[] = [
    { label: 'Platform group', colClass: 'col-md-4' },
    { label: 'Contact type', colClass: 'col-md-3' },
    { label: 'Snöball segment', colClass: 'col-md' },
  ];
  readonly mapsToColumns: SbComplexColumn[] = [
    { label: 'Snöball field', colClass: 'col-md-4' },
    { label: 'Direction', colClass: 'col-md-3' },
    { label: 'Platform field', colClass: 'col-md' },
  ];
  readonly conditionColumns: SbComplexColumn[] = [
    { label: 'Field', colClass: 'col-md-4' },
    { label: 'Operator', colClass: 'col-md-3' },
    { label: 'Value', colClass: 'col-md' },
  ];
  readonly conditionMatchItems: SbSelectItem[] = [
    { id: 'all', name: 'Match all rules' },
    { id: 'any', name: 'Match any rule' },
  ];

  readonly mappingEmptyRow: Values = { key: null, value: '' };
  /** The mapping's header, matching its row template's cells. */
  readonly mappingColumns: SbComplexColumn[] = [
    { label: 'Snöball field', colClass: 'col-md-5' },
    { label: 'Platform field', colClass: 'col-md' },
  ];

  private touched = new Set<string>();
  private prepared: Values = {};
  private clientErrors: Record<string, string> = {};
  private clientFormErrors: string[] = [];
  private serverFieldErrors: Record<string, string> = {};
  private serverFormErrors: string[] = [];
  private shownFormErrors: string[] = [];
  private publishPending = false;
  private itemsCache = new Map<string, SbSelectItem[]>();
  private selectItemsCache = new WeakMap<SbSchemaField, SbSelectItem[]>();
  private emptyRowCache = new WeakMap<SbSchemaField, Values>();
  private rowsColumnsCache = new WeakMap<SbSchemaField, SbComplexColumn[]>();
  private mappingRemovableCache = new WeakMap<SbSchemaField, (row: Values) => boolean>();
  private conditionItemsCache = new WeakMap<SbSchemaField, { fields: SbSelectItem[]; operators: SbSelectItem[] }>();
  private groupRowsCache = new Map<string, Values[]>();
  private fieldsByKey = new Map<string, SbSchemaField>();

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.schema) {
      this.selectItemsCache = new WeakMap();
      this.emptyRowCache = new WeakMap();
      this.rowsColumnsCache = new WeakMap();
      this.mappingRemovableCache = new WeakMap();
      this.conditionItemsCache = new WeakMap();
      this.groupRowsCache.clear();
      this.layout();
    }
    if (changes.fieldMapping || changes.schema) {
      this.groupFieldMapping();
    }
    if (changes.schema || changes.options) {
      this.itemsCache.clear();
    }
    if (changes.schema || changes.values) {
      this.reset();
    }
    const serverErrors = this.serverErrors();
    if (changes.serverErrors && serverErrors) {
      this.applyServerErrors(serverErrors);
    }
    this.publishLater();
  }

  get form(): SbSchemaForm {
    return this.schema() || EMPTY_FORM;
  }

  get fields(): SbSchemaField[] {
    return this.form.fields || [];
  }

  /**
   * The settings to save: numbers, booleans, rows as an array of complete rows, a mapping as an
   * object, cleared text as null. A blank secret is '' (keep the stored one). Notes and copy
   * fields are not settings.
   */
  get value(): Values {
    const prepared = prepareValues(this.form, this.model);
    const result: Values = {};
    for (const field of this.fields) {
      if (field.type === 'note' || field.type === 'copy') {
        continue;
      }
      const value = this.outputValue(field, prepared[field.key]);
      if (value !== undefined) {
        result[field.key] = value;
      }
    }
    return result;
  }

  /**
   * True when the current values pass every rule (whether or not messages are shown yet). Read it
   * in handlers: a new `values` input changes it while the host's view is being checked.
   */
  get valid(): boolean {
    return !Object.keys(this.clientErrors).length && !this.clientFormErrors.length;
  }

  /**
   * Form-wide messages for the host's banner: a refused save's, then the rules' once submitted.
   * `validate()`, `setServerErrors()` and edits update it at once. A change an input makes
   * (`values` resets the form, `serverErrors`) lands after the host's check, so a banner bound to
   * it earlier in the host's template never changes mid-check.
   */
  get formErrors(): string[] {
    return this.shownFormErrors;
  }

  /** Shows every message and reports whether the form may be saved. */
  validate(): boolean {
    this.submitted = true;
    this.serverFormErrors = [];
    this.recompute();
    this.publishFormErrors();
    // Hosts call this (and setServerErrors) from their own handlers, not from this template.
    this.cdr.markForCheck();
    return this.valid;
  }

  /**
   * Shows a refused save's messages. Field messages (`rows.N.sub` counting complete rows, as the
   * server does) stay until their field changes; a message for no visible field joins `formErrors`.
   */
  setServerErrors(payload: SbSchemaServerErrors | null): void {
    this.applyServerErrors(payload);
    this.publishFormErrors();
    this.cdr.markForCheck();
  }

  errorFor(path: string): string {
    if (this.serverFieldErrors[path]) {
      return this.serverFieldErrors[path];
    }
    if (!this.submitted && !this.touched.has(path)) {
      return '';
    }
    return this.clientErrors[path] || '';
  }

  markTouched(path: string): void {
    this.touched.add(path);
  }

  /** Leaving a field shows its messages; a rows, mapping or conditions pack waits for validate() or a row added or removed. */
  onFieldBlur(field: SbSchemaField): void {
    if (field.type !== 'rows' && field.type !== 'mapping' && field.type !== 'conditions') {
      this.markTouched(field.key);
    }
  }

  /** A section shows while it has a visible field; the mapping section also while it has read-only rows. */
  isSectionVisible(section: SbSchemaSectionView): boolean {
    return this.hasVisibleField(section.fields)
      || (section.key === SB_SCHEMA_FIELD_MAPPING_SECTION && this.mappedGroups.length > 0);
  }

  /** The visible fields of a fixed-row table. */
  groupFields(item: SbSchemaItem): SbSchemaField[] {
    return item.fields.filter((field) => this.isFieldVisible(field));
  }

  /** A fixed-row table's rows: one `{key}` per visible field, the same array while they stay the same. */
  groupRows(item: SbSchemaItem): Values[] {
    const keys = this.groupFields(item).map((field) => field.key);
    const cacheKey = `${item.key}|${keys.join(',')}`;
    let rows = this.groupRowsCache.get(cacheKey);
    if (!rows) {
      rows = keys.map((key) => ({ key }));
      this.groupRowsCache.set(cacheKey, rows);
    }
    return rows;
  }

  fieldByKey(key: unknown): SbSchemaField | null {
    return this.fieldsByKey.get(String(key)) || null;
  }

  /** A segment field's contact type, from its first influencer type: Attendee, Speaker or Partner. */
  contactType(field: SbSchemaField): string {
    return this.contactTypeName(field.influencerTypes?.[0]);
  }

  /** The Snöball model of a `conditions` field: its rules, as the pack edits them. */
  conditionRules(field: SbSchemaField): Values[] {
    const rules = (this.model[field.key] as Values)?.rules;
    return Array.isArray(rules) ? rules : [];
  }

  conditionMatch(field: SbSchemaField): string {
    return (this.model[field.key] as Values)?.match === 'any' ? 'any' : 'all';
  }

  conditionMaxRules(field: SbSchemaField): number {
    return field.maxRules || SB_SCHEMA_CONDITION_MAX_RULES;
  }

  /** A new rule: nothing chosen yet, so a rule left as it is gets dropped. */
  emptyRule(field: SbSchemaField): Values {
    let row = this.emptyRowCache.get(field);
    if (!row) {
      row = { field: field.fieldOptions?.length ? null : '', operator: null, value: '' };
      this.emptyRowCache.set(field, row);
    }
    return row;
  }

  /**
   * The Field select's list. `fieldOptions` are suggestions: unless `strictFields`, the select
   * also takes a typed field name, and a rule naming another field (typed, or a converted legacy
   * filter) gets that field added to its own row's list, so it shows.
   */
  conditionFieldItems(field: SbSchemaField, row: Values | null = null): SbSelectItem[] {
    const items = this.conditionItems(field).fields;
    const current = row?.field;
    if (typeof current !== 'string' || !current.trim() || items.some((item) => item.id === current)) {
      return items;
    }
    const cacheKey = `condition|${field.key}|${current}`;
    let extended = this.itemsCache.get(cacheKey);
    if (!extended) {
      extended = [...items, { id: current, name: current }];
      this.itemsCache.set(cacheKey, extended);
    }
    return extended;
  }

  /** How many read-only mapping rows the toggle reveals. */
  get mappedCount(): number {
    return this.mappedGroups.reduce((total, group) => total + group.rows.length, 0);
  }

  toggleMapped(): void {
    this.mappedExpanded = !this.mappedExpanded;
  }

  conditionOperatorItems(field: SbSchemaField): SbSelectItem[] {
    return this.conditionItems(field).operators;
  }

  /** The value cell shows only for an operator that compares against a value. */
  ruleNeedsValue(row: Values): boolean {
    return conditionNeedsValue(row?.operator ?? null);
  }

  rulePath(field: SbSchemaField, index: number, key: string): string {
    return `${field.key}.${index}.${key}`;
  }

  setConditionRules(field: SbSchemaField, rules: Values[]): void {
    this.setValue(field, { match: this.conditionMatch(field), rules });
  }

  setConditionMatch(field: SbSchemaField, match: unknown): void {
    this.setValue(field, { match: match === 'any' ? 'any' : 'all', rules: this.conditionRules(field) });
  }

  /** One cell of a rule; its server message goes with the edit. */
  setRuleValue(field: SbSchemaField, index: number, key: string, value: unknown, patch: RowPatch): void {
    delete this.serverFieldErrors[this.rulePath(field, index, key)];
    patch({ [key]: value });
  }

  isFieldVisible(field: SbSchemaField): boolean {
    return field.type !== 'hidden' && isVisible(field, this.prepared);
  }

  hasVisibleField(fields: SbSchemaField[]): boolean {
    return fields.some((field) => this.isFieldVisible(field));
  }

  /** Required as the server sees it: a configured secret is satisfied when left blank. */
  isRequired(field: SbSchemaField): boolean {
    return !!field.required && !(field.type === 'secret' && field.configured);
  }

  isRowsRequired(field: SbSchemaField): boolean {
    return this.isRequired(field) || (field.minItems || 0) > 0;
  }

  controlFor(field: SbSchemaField): SbSchemaControl {
    switch (field.type) {
      case 'secret':
      case 'number':
      case 'boolean':
      case 'radio':
      case 'select':
        return field.type;
      case 'segment':
        return 'select';
      case 'json':
      case 'textarea':
        return 'textarea';
      default:
        return 'text';
    }
  }

  /** A segment that follows a sibling (`influencerTypeFrom`) stays disabled until the sibling has a value. */
  awaitsSibling(field: SbSchemaField, row: Values | null = null): boolean {
    return field.type === 'segment' && !!field.influencerTypeFrom && isBlank((row || this.model)[field.influencerTypeFrom]);
  }

  selectPlaceholder(field: SbSchemaField, parent: SbSchemaField | null = null, row: Values | null = null): string {
    if (this.awaitsSibling(field, row)) {
      const sibling = (parent ? parent.fields || [] : this.fields).find((item) => item.key === field.influencerTypeFrom);
      if (sibling?.label) {
        return `Select ${sibling.label.toLowerCase()} first`;
      }
    }
    return field.placeholder || '';
  }

  /** The placeholder of a segment select in the segment mapping table. */
  segmentCellPlaceholder(field: SbSchemaField): string {
    return this.selectPlaceholder(field) || SB_SCHEMA_SEGMENT_PLACEHOLDER;
  }

  rowPath(field: SbSchemaField, index: number, subField: SbSchemaField): string {
    return `${field.key}.${index}.${subField.key}`;
  }

  rowsMinItems(field: SbSchemaField): number {
    return Math.max(field.minItems || 0, 1);
  }

  /** A new row: blank text, nothing selected, booleans left out so the row stays blank. */
  emptyRow(field: SbSchemaField): Values {
    let row = this.emptyRowCache.get(field);
    if (!row) {
      row = {};
      for (const subField of field.fields || []) {
        const control = this.controlFor(subField);
        if (control === 'select' || control === 'radio') {
          row[subField.key] = null;
        } else if (control !== 'boolean') {
          row[subField.key] = '';
        }
      }
      this.emptyRowCache.set(field, row);
    }
    return row;
  }

  /** The list a select-like control offers; `row` is the row a sub-field sits in. */
  itemsFor(field: SbSchemaField, row: Values | null = null): SbSelectItem[] {
    if (field.type === 'segment') {
      return this.segmentItems(field, row);
    }
    if (field.type === 'select') {
      return this.selectItems(field);
    }
    return NO_ITEMS;
  }

  selectItems(field: SbSchemaField): SbSelectItem[] {
    let items = this.selectItemsCache.get(field);
    if (!items) {
      items = (field.options || []).map((option) => ({
        id: option.value as string | number,
        name: option.label,
        description: option.description,
      }));
      this.selectItemsCache.set(field, items);
    }
    return items;
  }

  /**
   * The project's segments of the field's influencer types, or, inside rows, of the type its
   * sibling (`influencerTypeFrom`) holds. Cached so the select keeps one list per type set.
   */
  segmentItems(field: SbSchemaField, row: Values | null = null): SbSelectItem[] {
    const types = field.influencerTypeFrom
      ? [(row || this.model)[field.influencerTypeFrom]]
      : field.influencerTypes || [];
    const ids = types
      .map((type) => this.influencerTypeId(type))
      .filter((id): id is number => id !== null);
    const cacheKey = `segment|${ids.join(',')}`;
    let items = this.itemsCache.get(cacheKey);
    if (!items) {
      items = ids.length
        ? (this.options()?.segments || [])
          .filter((segment: SbSchemaSegment) => ids.includes(Number(segment.influencer_type_id)))
          .map((segment) => ({ id: segment.id, name: segment.name }))
        : NO_ITEMS;
      this.itemsCache.set(cacheKey, items);
    }
    return items;
  }

  /** Mapping keys a row may choose: every key option no other row has taken. */
  mappingKeyItems(field: SbSchemaField, index: number): SbSelectItem[] {
    const rows = this.mappingRows(field);
    const taken = new Set(rows
      .filter((row, position) => position !== index && row?.key != null)
      .map((row) => String(row.key)));
    const available = (field.keyOptions || []).filter((option) => !taken.has(String(option.value)));
    const cacheKey = `mapping|${field.key}|${available.map((option) => String(option.value)).join('\u0001')}`;
    let items = this.itemsCache.get(cacheKey);
    if (!items) {
      items = available.map((option) => ({ id: option.value as string | number, name: option.label }));
      this.itemsCache.set(cacheKey, items);
    }
    return items;
  }

  isRequiredMappingRow(field: SbSchemaField, row: Values): boolean {
    return row?.key != null && (field.requiredKeys || []).includes(String(row.key));
  }

  /** For the pack: a required key's row stays; the pack itself keeps at least one row. */
  mappingRowRemovable(field: SbSchemaField): (row: Values) => boolean {
    let removable = this.mappingRemovableCache.get(field);
    if (!removable) {
      removable = (row: Values) => !this.isRequiredMappingRow(field, row);
      this.mappingRemovableCache.set(field, removable);
    }
    return removable;
  }

  /** Each key can be mapped once, so there are never more rows than keys. */
  mappingMaxItems(field: SbSchemaField): number | null {
    return field.keyOptions?.length || null;
  }

  /** The rows field's header: one column per sub-field, matching the row template's `col-md` cells. */
  rowsColumns(field: SbSchemaField): SbComplexColumn[] {
    let columns = this.rowsColumnsCache.get(field);
    if (!columns) {
      columns = (field.fields || []).map((subField) => ({
        label: subField.label || '',
        colClass: 'col-md',
        required: this.isRequired(subField),
      }));
      this.rowsColumnsCache.set(field, columns);
    }
    return columns;
  }

  onControlChange(
    field: SbSchemaField,
    value: unknown,
    parent: SbSchemaField | null = null,
    row: Values | null = null,
    index: number | null = null,
    patch: RowPatch | null = null,
  ): void {
    if (parent && patch && index !== null) {
      this.setRowValue(parent, row || {}, index, field, value, patch);
      return;
    }
    this.setValue(field, value);
  }

  setValue(field: SbSchemaField, value: unknown): void {
    const previous = this.model[field.key];
    this.model[field.key] = value;
    delete this.serverFieldErrors[field.key];
    const listOf = (item: unknown): unknown => (field.type === 'conditions' ? (item as Values)?.rules : item);
    const before = listOf(previous);
    const after = listOf(value);
    if ((field.type === 'rows' || field.type === 'mapping' || field.type === 'conditions')
      && (!Array.isArray(before) || !Array.isArray(after) || before.length !== after.length)) {
      // A row was added or removed: messages for row positions no longer point at their rows.
      this.clearServerErrors(`${field.key}.`);
      this.markTouched(field.key);
    }
    if (previous !== value) {
      for (const dependent of this.fields) {
        if (dependent.type === 'segment' && dependent.influencerTypeFrom === field.key) {
          this.model[dependent.key] = null;
          delete this.serverFieldErrors[dependent.key];
        }
      }
    }
    this.recompute();
    this.publishFormErrors();
    // Public, like validate(): a host may set a value from outside this template.
    this.cdr.markForCheck();
    this.valueChange.emit(this.value);
  }

  /** A sub-field edit inside rows; a segment that follows the edited sibling is cleared with it. */
  setRowValue(field: SbSchemaField, row: Values, index: number, subField: SbSchemaField, value: unknown, patch: RowPatch): void {
    const partial: Values = { [subField.key]: value };
    delete this.serverFieldErrors[this.rowPath(field, index, subField)];
    if (row[subField.key] !== value) {
      for (const sibling of field.fields || []) {
        if (sibling.type === 'segment' && sibling.influencerTypeFrom === subField.key) {
          partial[sibling.key] = null;
          delete this.serverFieldErrors[this.rowPath(field, index, sibling)];
        }
      }
    }
    patch(partial);
  }

  trackByKey(_index: number, item: { key: string }): string {
    return item.key;
  }

  private layout(): void {
    const renderable = this.fields.filter((field) => field.type !== 'hidden');
    const sections = this.form.sections || [];
    const known = new Set(sections.map((section) => section.key));
    this.fieldsByKey = new Map(this.fields.map((field) => [field.key, field]));
    this.looseFields = renderable.filter((field) => !field.section || !known.has(field.section));
    this.looseItems = this.itemsOf(this.looseFields);
    const views: SbSchemaSectionView[] = sections
      .map((section) => {
        const fields = renderable.filter((field) => field.section === section.key);
        return {
          key: section.key,
          title: section.title,
          description: section.description || '',
          fields,
          items: this.itemsOf(fields),
        };
      })
      .filter((section) => section.fields.length || section.key === SB_SCHEMA_FIELD_MAPPING_SECTION);
    if (!views.some((section) => section.key === SB_SCHEMA_FIELD_MAPPING_SECTION)) {
      // Its own section right after the segments (else the connection), for the read-only rows.
      const anchor = [views.findIndex((section) => section.key === 'segments'), views.findIndex((section) => section.key === 'connection')]
        .find((index) => index >= 0);
      views.splice(anchor === undefined ? 0 : anchor + 1, 0, {
        key: SB_SCHEMA_FIELD_MAPPING_SECTION,
        title: 'Field mapping',
        description: 'How platform fields fill Snöball contact fields.',
        fields: [],
        items: [],
      });
    }
    this.sectionViews = views;
  }

  /** Groups consecutive segment fields, and consecutive field-name settings, into one table each. */
  private itemsOf(fields: SbSchemaField[]): SbSchemaItem[] {
    const items: SbSchemaItem[] = [];
    for (const field of fields) {
      const kind: SbSchemaItem['kind'] = field.type === 'segment' ? 'segments' : field.mapsTo ? 'mapsTo' : 'field';
      const last = items[items.length - 1];
      if (kind !== 'field' && last && last.kind === kind) {
        last.fields.push(field);
        continue;
      }
      items.push({
        key: kind === 'field' ? field.key : `${kind}:${field.key}`,
        kind,
        field: kind === 'field' ? field : null,
        fields: [field],
      });
    }
    return items;
  }

  /**
   * `fieldMapping` merged by contact type, leaving out repeated rows and the rows a rendered setting
   * edits (a row whose setting is hidden or absent stays, as nothing else shows it).
   */
  private groupFieldMapping(): void {
    const byType = new Map<string, SbSchemaMappedGroup>();
    const fieldMapping = this.fieldMapping();
    for (const group of Array.isArray(fieldMapping) ? fieldMapping : []) {
      const type = String(group?.influencerType || '');
      let view = byType.get(type);
      if (!view) {
        view = { influencerType: type, title: type ? `${this.contactTypeName(type)} contacts` : 'Contacts', rows: [] };
        byType.set(type, view);
      }
      for (const row of group?.fields || []) {
        if (!row || (row.settingKey && this.isRenderedSetting(row.settingKey))) {
          continue;
        }
        if (!view.rows.some((seen) => seen.target === row.target && seen.source === row.source)) {
          view.rows.push(row);
        }
      }
    }
    this.mappedGroups = Array.from(byType.values()).filter((view) => view.rows.length);
  }

  private isRenderedSetting(key: string): boolean {
    const field = this.fieldsByKey.get(String(key));
    return !!field && field.type !== 'hidden';
  }

  private contactTypeName(type: unknown): string {
    const slug = type == null ? '' : String(type);
    if (CONTACT_TYPES[slug]) {
      return CONTACT_TYPES[slug];
    }
    return slug ? slug.charAt(0).toUpperCase() + slug.slice(1) : '';
  }

  private conditionItems(field: SbSchemaField): { fields: SbSelectItem[]; operators: SbSelectItem[] } {
    let items = this.conditionItemsCache.get(field);
    if (!items) {
      items = {
        fields: (field.fieldOptions || []).map((option) => ({ id: option.value as string | number, name: option.label })),
        operators: conditionOperators(field).map((operator) => ({ id: operator.id, name: operator.label })),
      };
      this.conditionItemsCache.set(field, items);
    }
    return items;
  }

  private reset(): void {
    this.touched.clear();
    this.submitted = false;
    this.serverFieldErrors = {};
    this.serverFormErrors = [];
    const prepared = prepareValues(this.form, this.values() || {});
    const model: Values = {};
    for (const field of this.fields) {
      model[field.key] = this.controlValue(field, prepared[field.key], prepared);
    }
    this.model = model;
    this.recompute();
  }

  private recompute(): void {
    this.prepared = prepareValues(this.form, this.model);
    const configuredSecrets = this.fields
      .filter((field) => field.type === 'secret' && field.configured)
      .map((field) => field.key);
    const result = validateValues(this.form, this.prepared, { configuredSecrets });
    const errors: Record<string, string> = {};
    for (const [key, message] of Object.entries(result.errors)) {
      errors[this.uiPath(key) || key] = message;
    }
    this.clientErrors = errors;
    this.clientFormErrors = result.formErrors;
  }

  private publishFormErrors(): void {
    const messages = [...this.serverFormErrors];
    if (this.submitted) {
      for (const message of this.clientFormErrors) {
        if (!messages.includes(message)) {
          messages.push(message);
        }
      }
    }
    const shown = this.shownFormErrors;
    if (messages.length !== shown.length || messages.some((message, index) => message !== shown[index])) {
      this.shownFormErrors = messages;
      this.formErrorsChange.emit(messages);
    }
  }

  /** Publishes after the current change detection, which may already have read `formErrors`. */
  private publishLater(): void {
    if (this.publishPending) {
      return;
    }
    this.publishPending = true;
    Promise.resolve().then(() => {
      this.publishPending = false;
      this.publishFormErrors();
    });
  }

  /** The control's value for a prepared setting. */
  private controlValue(field: SbSchemaField, value: unknown, prepared: Values): unknown {
    switch (field.type) {
      case 'secret':
        return '';
      case 'text':
      case 'url':
      case 'textarea':
      case 'number':
        return value == null ? '' : String(value);
      case 'json':
        if (value == null) {
          return '';
        }
        return typeof value === 'string' ? value : JSON.stringify(value, null, 2);
      case 'boolean':
        return value === true;
      case 'radio':
      case 'select': {
        const option = (field.options || []).find((item) => String(item.value) === String(value));
        return option ? option.value : value ?? null;
      }
      case 'segment':
        return value ?? null;
      case 'copy': {
        const shown = isBlank(value) && field.source ? prepared[field.source] : value;
        return shown == null ? '' : String(shown);
      }
      case 'rows': {
        const rows = (Array.isArray(value) ? value : [])
          .filter((row) => row && typeof row === 'object' && !Array.isArray(row))
          .map((row: Values) => {
            const result: Values = { ...row };
            for (const subField of field.fields || []) {
              result[subField.key] = this.controlValue(subField, row[subField.key], row);
            }
            return result;
          });
        while (rows.length < this.rowsMinItems(field)) {
          rows.push({ ...this.emptyRow(field) });
        }
        return rows;
      }
      case 'mapping': {
        const pairs: Values = value && typeof value === 'object' && !Array.isArray(value) ? value as Values : {};
        const text = (key: string): string => (pairs[key] == null ? '' : String(pairs[key]));
        const requiredKeys = field.requiredKeys || [];
        const rows = [
          ...requiredKeys.map((key) => ({ key, value: text(key) })),
          ...Object.keys(pairs).filter((key) => !requiredKeys.includes(key)).map((key) => ({ key, value: text(key) })),
        ];
        return rows.length ? rows : [{ ...this.mappingEmptyRow }];
      }
      case 'conditions': {
        const source: Values = value && typeof value === 'object' && !Array.isArray(value) ? value as Values : {};
        const rules = (Array.isArray(source.rules) ? source.rules : [])
          .filter((rule) => rule && typeof rule === 'object' && !Array.isArray(rule))
          .map((rule: Values) => ({
            field: rule.field == null ? this.emptyRule(field).field : rule.field,
            operator: rule.operator ?? null,
            value: rule.value == null ? '' : String(rule.value),
          }));
        return { match: source.match === 'any' ? 'any' : 'all', rules };
      }
      default:
        return value;
    }
  }

  /** A prepared setting as a save sends it. */
  private outputValue(field: SbSchemaField, value: unknown): unknown {
    switch (field.type) {
      case 'secret':
        return value == null ? '' : value;
      case 'text':
      case 'url':
      case 'textarea':
      case 'json':
        return value === undefined ? null : value;
      case 'number':
      case 'segment':
        return value == null || value === '' ? null : value;
      case 'boolean':
        return value === true;
      case 'radio':
      case 'select':
        return value === undefined ? null : value;
      case 'rows':
        return Array.isArray(value) ? value : [];
      case 'mapping':
        return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
      case 'conditions':
        // No rule left is null: no filter, every record syncs.
        return value === undefined ? null : value;
      default:
        return value;
    }
  }

  private mappingRows(field: SbSchemaField): Values[] {
    const rows = this.model[field.key];
    return Array.isArray(rows) ? rows : [];
  }

  /** `rows.N.sub` (N counting complete rows) as the path of the row on screen, else the key itself. */
  private uiPath(key: string): string | null {
    const match = ROW_PATH.exec(key);
    if (!match) {
      return key;
    }
    const field = this.fields.find((item) => item.key === match[1]);
    if (field && field.type === 'conditions') {
      const kept = keptRuleIndexes((this.model[field.key] as Values)?.rules)[Number(match[2])];
      return kept === undefined ? null : `${field.key}.${kept}.${match[3]}`;
    }
    if (!field || field.type !== 'rows') {
      return key;
    }
    const position = keptRowIndexes(field, this.model[field.key])[Number(match[2])];
    return position === undefined ? null : `${field.key}.${position}.${match[3]}`;
  }

  /** Where a server message shows: a visible field (or row sub-field), else nowhere (null). */
  private serverPath(key: string): string | null {
    const match = ROW_PATH.exec(key);
    const field = this.fields.find((item) => item.key === (match ? match[1] : key));
    if (!field || !this.isFieldVisible(field) || field.type === 'note' || field.type === 'copy') {
      return null;
    }
    if (!match) {
      return key;
    }
    if (field.type === 'conditions') {
      return CONDITION_SUB_KEYS.includes(match[3]) ? this.uiPath(key) : null;
    }
    if (field.type !== 'rows' || !(field.fields || []).some((subField) => subField.key === match[3])) {
      return null;
    }
    return this.uiPath(key);
  }

  private applyServerErrors(payload: SbSchemaServerErrors | null): void {
    this.serverFieldErrors = {};
    this.serverFormErrors = [];
    for (const [key, message] of Object.entries(payload?.errors || {})) {
      if (typeof message !== 'string' || !message) {
        continue;
      }
      const path = this.serverPath(key);
      if (path) {
        this.serverFieldErrors[path] = message;
      } else {
        this.addFormError(message);
      }
    }
    for (const message of payload?.form_errors || []) {
      if (typeof message === 'string' && message) {
        this.addFormError(message);
      }
    }
  }

  private clearServerErrors(prefix: string): void {
    for (const path of Object.keys(this.serverFieldErrors)) {
      if (path.startsWith(prefix)) {
        delete this.serverFieldErrors[path];
      }
    }
  }

  private addFormError(message: string): void {
    if (!this.serverFormErrors.includes(message)) {
      this.serverFormErrors.push(message);
    }
  }

  private influencerTypeId(type: unknown): number | null {
    if (type == null || type === '') {
      return null;
    }
    const known = this.options()?.influencer_types?.[String(type)];
    if (known !== undefined) {
      return Number(known);
    }
    const id = Number(type);
    return Number.isInteger(id) && id > 0 ? id : null;
  }
}
